import { useMemo } from 'react';
import type { Leaseholder, LeaseholderFilter } from '../types/leaseholder';
import type { SortField, SortDirection } from '../components/Leaseholders/SortControls';

export function useSortedLeaseholders(
  leaseholders: Leaseholder[] | undefined,
  sortField: SortField | string,
  sortDirection: SortDirection,
  searchQuery: string,
  filters?: LeaseholderFilter
) {
  return useMemo(() => {
    if (!leaseholders) return [];

    // First filter by search query
    let filtered = leaseholders.filter(
      (lh) => {
        const searchStr = searchQuery.toLowerCase();
        return (
          lh.holder_name.toLowerCase().includes(searchStr) ||
          lh.holder_position?.toLowerCase().includes(searchStr) ||
          lh.holder_email?.toLowerCase().includes(searchStr) ||
          lh.holder_abn?.toString().includes(searchStr) ||
          lh.holder_contact?.toLowerCase().includes(searchStr) ||
          lh.holder_phone?.toLowerCase().includes(searchStr)
        );
      }
    );

    // Apply additional filters if provided
    if (filters) {
      if (filters.position) {
        const positionFilter = filters.position;
        filtered = filtered.filter(lh => 
          lh.holder_position?.toLowerCase() === positionFilter.toLowerCase()
        );
      }

      if (filters.state) {
        const stateFilter = filters.state;
        filtered = filtered.filter(lh => 
          lh.holder_phy_state?.toLowerCase() === stateFilter.toLowerCase() ||
          lh.holder_post_state?.toLowerCase() === stateFilter.toLowerCase()
        );
      }

      if (filters.hasABN !== null) {
        const hasABNFilter = filters.hasABN;
        filtered = filtered.filter(lh => 
          hasABNFilter ? !!lh.holder_abn : !lh.holder_abn
        );
      }
    }

    // Then sort
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'name':
          comparison = a.holder_name.localeCompare(b.holder_name);
          break;
        case 'holder_id':
          comparison = a.holder_id - b.holder_id;
          break;
        case 'lot_count':
          comparison = (a.lot_count || 0) - (b.lot_count || 0);
          break;
        case 'indicative_annual_value':
          comparison = (a.indicative_annual_value || 0) - (b.indicative_annual_value || 0);
          break;
        default:
          comparison = a.holder_name.localeCompare(b.holder_name);
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [leaseholders, sortField, sortDirection, searchQuery, filters]);
}
